import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUtmTaxonomyStore, type Group } from '@/store/utm-taxonomy-store';
import UtmDataTable from '@/components/utm/shared/UtmDataTable';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Icon, type IconName } from '@/components/utm/shared/UtmIconPicker';
import { resolveUtmIconUrl } from '@/components/utm/shared/utm-icon-url';

const GroupIconCell = ({ group }: { group: Group }) => {
  const iconUrl = group.iconType === 'S3_OBJECT' ? resolveUtmIconUrl(group.iconValue) : null;

  return (
    <div className="flex items-center">
      {group.iconType === 'BUILTIN' ? (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: group.iconBgColor || '#1e293b' }}
        >
          <Icon
            name={group.iconValue as IconName}
            size={20}
            style={{ color: group.iconColor || '#fff' }}
          />
        </div>
      ) : group.iconType === 'S3_OBJECT' && iconUrl ? (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: group.iconBgColor || '#1e293b' }}
        >
          <img src={iconUrl} alt={group.name} className="w-8 h-8 object-contain" />
        </div>
      ) : (
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center" />
      )}
    </div>
  );
};

const UtmGroupList = () => {
  const navigate = useNavigate();
  const { categories, groups, deleteGroup, isLoading } = useUtmTaxonomyStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const filteredGroups = groups.filter((group) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      group.name.toLowerCase().includes(query) ||
      group.code.toLowerCase().includes(query) ||
      group.description?.toLowerCase().includes(query);

    const matchesCategory = !categoryFilter || group.categoryId === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  const handleDelete = async () => {
    if (!groupToDelete) return;

    try {
      await deleteGroup(groupToDelete);
      setDeleteModalOpen(false);
      setGroupToDelete(null);
    } catch (error) {
      console.error('Failed to delete group:', error);
      alert('Failed to delete group. It may have associated zones.');
    }
  };

  const openDeleteModal = (id: string) => {
    setGroupToDelete(id);
    setDeleteModalOpen(true);
  };

  const columns = [
    {
      key: 'icon',
      label: 'Icon',
      render: (group: Group) => <GroupIconCell group={group} />,
    },
    {
      key: 'name',
      label: 'Name',
      render: (group: Group) => (
        <div>
          <div className="font-medium text-foreground">{group.name}</div>
          <div className="text-xs text-muted-foreground mt-1">{group.code}</div>
          {group.description && (
            <div className="text-xs text-muted-foreground mt-1">{group.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      render: (group: Group) => {
        const category = categories.find((c) => c.id === group.categoryId);
        return <span className="text-muted-foreground">{category?.name || 'Unknown'}</span>;
      },
    },
  ];

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Groups</h1>
          <p className="text-muted-foreground mt-1">Manage UTM zone groups within categories</p>
        </div>
        <button
          onClick={() => navigate('/admin/uzamaps/groups/new')}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
        >
          <Plus size={18} />
          <span>Add Group</span>
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-foreground mb-2">
          Filter by Category
        </label>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:border-ring focus:outline-none sm:w-auto"
        >
          <option value="">All Categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Data Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground">Loading groups...</div>
        </div>
      ) : (
        <UtmDataTable
          data={filteredGroups}
          columns={columns}
          searchPlaceholder="Search groups..."
          onSearch={setSearchQuery}
          itemsPerPage={10}
          mobileCardRender={(group) => {
            const category = categories.find((c) => c.id === group.categoryId);
            return (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <GroupIconCell group={group} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-foreground">{group.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{group.code}</div>
                    {group.description && (
                      <div className="mt-1 text-xs text-muted-foreground">{group.description}</div>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      Category: {category?.name || 'Unknown'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/uzamaps/groups/${group.id}/edit`)}
                    className="rounded p-2 text-primary transition-colors hover:bg-accent hover:text-primary/80"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => openDeleteModal(group.id)}
                    className="rounded p-2 text-destructive transition-colors hover:bg-accent hover:text-destructive/80"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          }}
          actions={(group) => (
            <>
              <button
                onClick={() =>
                  navigate(`/admin/uzamaps/groups/${group.id}/edit`)
                }
                className="p-2 text-primary hover:text-primary/80 hover:bg-accent rounded transition-colors"
                title="Edit"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => openDeleteModal(group.id)}
                className="p-2 text-destructive hover:text-destructive/80 hover:bg-accent rounded transition-colors"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-foreground mb-2">Delete Group</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this group? This action cannot be undone. All
              associated zones will also be deleted.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setGroupToDelete(null);
                }}
                className="px-4 py-2 bg-muted hover:bg-accent text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtmGroupList;
